<template>
  <div
    class="flex flex-col justify-center items-center gap-y-4 sm:gap-y-0 sm:flex-row sm:items-start sm:justify-start sm:gap-x-4"
  >
    <!-- Time Selector -->
    <PrimeButton
      id="dateFilterToggleButton"
      @click="$refs.dateRangePopover?.toggle($event)"
      severity="secondary"
    >
      <span>{{ dateFilterButtonText }}</span>
      <ChevronDown class="w-4 h-4" />
    </PrimeButton>
    <Popover
      ref="dateRangePopover"
      id="dateFilterModal"
      pt:content:class="md:flex md:flex-col md:gap-4 !p-6"
    >
      <div>
        <LabelDefault>Date Range</LabelDefault>
        <DatePicker
          id="datePicker"
          inline
          :numberOfMonths="2"
          :responsiveOptions="[{ breakpoint: '640px', numMonths: 1 }]"
          selectionMode="range"
          v-model="dateRange"
        />
      </div>
      <div
        class="mt-4 pt-4 border-t border-linear-subtle flex flex-col items-start gap-2 sm:flex-row sm:items-center justify-start sticky bottom-0 md:border-none md:mt-0 sm:gap-4 sm:pt-0"
      >
        <PrimeButton
          id="last28DaysButton"
          @click="setDateFilterType('last-28-days')"
          severity="secondary"
        >
          Last 28 Days
        </PrimeButton>
        <PrimeButton
          id="next28DaysButton"
          @click="setDateFilterType('next-28-days')"
          severity="secondary"
        >
          Next 28 Days
        </PrimeButton>
        <PrimeButton
          id="submitCustomDateRangeButton"
          @click="setDateFilterType('custom')"
          class="md:ml-auto"
          severity="secondary"
          :disabled="!dateRangeISO || dateRangeISO.some(date => !date)"
        >
          Apply Custom Date
        </PrimeButton>
      </div>
    </Popover>
    <!-- Search -->
    <TextField
      id="reportsTableSearchInput"
      :modelValue="searchQuery"
      placeholder="Search"
      @update:modelValue="onSearch"
      class="py-0"
    />
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import { mapStores } from 'pinia'
import { default as PrimeButton } from 'primevue/button'
import DatePicker from 'primevue/datepicker'
import Popover from 'primevue/popover'
import { computed, defineComponent, ref } from 'vue'

import ChevronDown from '@/assets/chevronDown.svg?component'
import LabelDefault from '@/common/components/atoms/LabelDefault.vue'
import TextField from '@/common/components/molecules/TextField.vue'
import { DateRangeISO, useDateRange } from '@/common/composables/useDatetime'
import { useSettingStore } from '@/stores/setting'
import { cleanString } from '@/utils/utils'

type DateFilterType = 'last-28-days' | 'next-28-days' | 'custom'
type DateFilter = {
  startDate: string
  endDate: string
}

export default defineComponent({
  name: 'ReportsTableFilter',
  components: {
    TextField,
    PrimeButton,
    Popover,
    LabelDefault,
    DatePicker,
    ChevronDown,
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    filterByDateRange: (dateFilter: DateFilter) => true,
    // eslint-disable-next-line no-unused-vars
    search: (searchQuery: string) => true,
  },
  data() {
    return {
      searchQuery: '',
      dateFilterType: 'next-28-days' as DateFilterType,
    }
  },
  setup() {
    const dateRangeISO = ref<DateRangeISO>()
    const { dateRange } = useDateRange(dateRangeISO, (value: DateRangeISO) => {
      dateRangeISO.value = value
    })
    const startDate = computed(() => dateRangeISO.value?.[0])
    const endDate = computed(() => dateRangeISO.value?.[1])
    return { dateRange, dateRangeISO, startDate, endDate }
  },
  computed: {
    ...mapStores(useSettingStore),
    dateFilterButtonText(): string {
      if (this.dateFilterType === 'last-28-days') {
        return 'Last 28 Days'
      } else if (this.dateFilterType === 'next-28-days') {
        return 'Next 28 Days'
      } else if (this.dateFilterType === 'custom') {
        if (!this.startDate || !this.endDate) return 'Date Filter'
        return `${this.settingStore.shortDate(this.startDate)} - ${this.settingStore.shortDate(
          this.endDate,
        )}`
      } else {
        return 'Date Filter'
      }
    },
    dateFilter(): DateFilter {
      switch (this.dateFilterType) {
        case 'last-28-days': {
          const yesterday = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD')
          return {
            startDate: moment(yesterday).subtract(28, 'days').format('YYYY-MM-DD'),
            endDate: yesterday,
          }
        }
        case 'next-28-days': {
          const today = moment(new Date()).format('YYYY-MM-DD')
          return {
            startDate: today,
            endDate: moment(today).add(28, 'days').format('YYYY-MM-DD'),
          }
        }
        case 'custom':
          return {
            startDate: this.startDate ?? '',
            endDate: this.endDate ?? '',
          }
        default:
          return {
            startDate: '',
            endDate: '',
          }
      }
    },
  },
  mounted() {
    // @ts-ignore
    this.setDateFilterType(this.dateFilterType)
  },
  methods: {
    setDateFilterType(dateFilterType: DateFilterType) {
      this.dateFilterType = dateFilterType
      this.$refs.dateRangePopover?.hide()
      this.$emit('filterByDateRange', this.dateFilter)
    },
    onSearch(query: string) {
      this.searchQuery = query
      this.$emit('search', cleanString(this.searchQuery))
    },
  },
})
</script>
